<script>
  import { closeModal, overlayModal } from '$lib/features/app/modals';
  import { onMount } from 'svelte';
  import { fade, fly } from 'svelte/transition';
  import { twMerge } from 'tailwind-merge';
  import Icon from './Icon.svelte';

  let {
    size = 'lg',
    title = undefined,
    showCloseButton = true,
    handleClose = null,
    blur = false,
    isOverlay = false,
    class: className = '',
    children = undefined,
  } = $props();

  onMount(() => {
    document.body.style.overflow = 'hidden';
    return () => (document.body.style.overflow = '');
  });

  const sizes = $derived(
    twMerge([
      size === 'sm' && 'max-w-sm',
      size === 'md' && 'max-w-lg',
      size === 'lg' && 'max-w-2xl',
      size === 'xl' && 'max-w-4xl',
    ]),
  );

  const classes = $derived(
    twMerge([
      'fixed inset-0 z-[1005] bg-black bg-opacity-60',
      blur && 'bg-opacity-30 bg-gray-900 backdrop-filter backdrop-blur-sm',
    ]),
  );

  function handleKeyDown(event) {
    if (event.key !== 'Escape') return;
    // Only the topmost modal responds to Esc. When the overlay is open and
    // we're not it, defer so pressing Esc dismisses the overlay only.
    if (!isOverlay && $overlayModal) return;
    onClose();
  }

  function onClose() {
    if (blur) return;
    if (handleClose) {
      handleClose();
    } else {
      closeModal();
    }
  }
</script>

<svelte:window onkeydown={handleKeyDown} />

<div transition:fade class={classes}>
  <div class="flex min-h-screen w-full">
    {#if !blur}
      <!-- Click-through backdrop: role="presentation" because it is a
           dismiss target, not a control. Keyboard dismissal is on
           <svelte:window onkeydown>. -->
      <div
        class="absolute inset-0"
        role="presentation"
        onkeydown={handleKeyDown}
        onclick={onClose}
      ></div>
    {/if}
    <div
      class="grid max-h-[100vh] w-full place-items-center overflow-y-auto pb-20 pt-20"
    >
      <div
        in:fly={{ y: 15, delay: 150 }}
        out:fly={{ y: -10 }}
        class="relative mx-4 flex w-full flex-col rounded-sm bg-white dark:bg-gray-9 {sizes}"
      >
        {#if title}
          <div class="w-full border-b p-2 text-center">
            <div class="font-mono dark:text-white">{title}</div>
          </div>
        {/if}

        <div class="w-full p-4 dark:text-gray-3 {className}">
          {@render children?.()}
        </div>

        {#if showCloseButton}
          <button
            class="absolute right-0 top-0.5 p-2 transition dark:text-gray-5 dark:hover:text-white"
            onclick={onClose}
          >
            <Icon icon="close" />
          </button>
        {/if}
      </div>
    </div>
  </div>
</div>
